import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AvdbPricingBundleType } from '@garmin-avcloud/avcloud-fly-web-common/api';
import { HeaderCartService } from '@garmin-avcloud/flyGarminAppNavigation';
import { Observable } from 'rxjs';
import { mergeMap, switchMap, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AvdbBundleCartItem } from '../models/cart/avdb-bundle-cart-item';
import { AvdbCartItem } from '../models/cart/avdb-cart-item.model';
import { CartAddItemRequest } from '../models/cart/cart-add-item-request';
import { Cart } from '../models/cart/cart.model';
import { CategorizedCart } from '../models/cart/categorized-cart.model';
import { CheckoutCartResponse } from '../models/cart/checkout-cart-response.model';
import { DiscountCodeCartItem } from '../models/cart/discount-code-cart-item';
import { EnablementCartItem } from '../models/cart/enablement-cart-item';
import { PilotCartItem } from '../models/cart/pilot-cart-item';
import { CollectionsService } from './collections.service';
import { LocaleService } from './locale.service';

const API_URL = `${environment.flyLegacyUrl}/api/`;
const CARTS_URL = `${API_URL}carts/`;
const CURRENT_CART_URL = `${CARTS_URL}current`;
const NEW_CART_URL = `${CARTS_URL}new`;
const LATEST_COMPLETED_CART_URL = `${CARTS_URL}latest-completed`;
const PRODUCTS_URL = `${CURRENT_CART_URL}/products`;

const PARSEINT_RADIX = 10;

@Injectable()
export class CartService {
  private readonly http = inject(HttpClient);
  private readonly collectionsService = inject(CollectionsService);
  private readonly localeService = inject(LocaleService);
  private readonly headerCartService = inject(HeaderCartService);

  createNewCart(): Observable<Cart> {
    return this.http.post<Cart>(NEW_CART_URL, null).pipe(tap(() => this.headerCartService.refreshCart()));
  }

  getCurrentCart(): Observable<Cart> {
    return this.http.get<Cart>(CURRENT_CART_URL);
  }

  getCartAndOrderInfoByOrderToken(orderToken: string): Observable<Cart> {
    return this.localeService.getSelectedLocale().pipe(
      mergeMap((locale) => {
        const params: Record<string, any> = {};
        params.orderToken = orderToken;
        params.locale = locale.wlcLocale;
        return this.http.get<Cart>(CARTS_URL, { params });
      })
    );
  }

  completeCartByOrderToken(orderToken: string): Observable<Cart> {
    return this.localeService.getSelectedLocale().pipe(
      mergeMap((locale) => {
        const params: Record<string, any> = {};
        params.locale = locale.wlcLocale;
        return this.http.put<Cart>(`${CARTS_URL}order/${orderToken}/complete`, null, { params });
      })
    );
  }

  addItemToCurrentCart(item: CartAddItemRequest): Observable<Cart> {
    return this.http
      .post<Cart>(`${CURRENT_CART_URL}/products`, item)
      .pipe(tap(() => this.headerCartService.refreshCart()));
  }

  removeAvdbFromCurrentCart(avdbId: string): Observable<Cart> {
    const avdbIdNum = parseInt(avdbId, PARSEINT_RADIX);
    const params = { avdbId: avdbIdNum };
    return this.removeItemFromCurrentCart(params);
  }

  removeAvdbBundleFromCurrentCart(avdbBundleId: string): Observable<Cart> {
    const avdbBundleIdNum = parseInt(avdbBundleId, PARSEINT_RADIX);
    const params = { avdbBundleId: avdbBundleIdNum };
    return this.removeItemFromCurrentCart(params);
  }

  removeGpItemFromCurrentCart(gpId: string): Observable<Cart> {
    const gpIdNum = parseInt(gpId, PARSEINT_RADIX);
    const params = { gpId: gpIdNum };
    return this.removeItemFromCurrentCart(params);
  }

  removeEnablementFromCurrentCart(enablementId: string): Observable<Cart> {
    const enablementIdNum = parseInt(enablementId, PARSEINT_RADIX);
    const params = { enablementId: enablementIdNum };
    return this.removeItemFromCurrentCart(params);
  }

  removePromoCodeFromCurrentCart(promoId: string): Observable<Cart> {
    const promoIdNum = parseInt(promoId, PARSEINT_RADIX);
    const params = { promoId: promoIdNum };
    return this.removeItemFromCurrentCart(params);
  }

  categorizeCartItems(cart: Cart): CategorizedCart {
    const gpCart: PilotCartItem[] = cart.gpCart;
    const avdbCart: AvdbCartItem[] = cart.avdbCart;
    const avdbBundleCart: AvdbBundleCartItem[] = cart.avdbBundleCart;
    const enablementCart: EnablementCartItem[] = cart.enablementCart;
    const discountCodeCart: DiscountCodeCartItem[] = cart.promoCodeCart;

    const aircraftBundlesCart: AvdbBundleCartItem[] = [];
    const deviceBundlesCart: AvdbBundleCartItem[] = [];

    for (const avdbBundleCartItem of avdbBundleCart) {
      switch (avdbBundleCartItem.bundle.bundleType) {
        case AvdbPricingBundleType.PILOTPAK:
        case AvdbPricingBundleType.ONEPAK:
          aircraftBundlesCart.push(avdbBundleCartItem);
          break;
        case AvdbPricingBundleType.DEVICE:
          deviceBundlesCart.push(avdbBundleCartItem);
          break;
        default:
      }
    }

    const aircraftBundleGroupedByAircraft = this.collectionsService.groupBy(
      aircraftBundlesCart,
      (bundle) => bundle.aircraft?.id ?? ''
    );
    const deviceBundleGroupedByDevice = this.collectionsService.groupBy(
      deviceBundlesCart,
      (bundle) => bundle.device?.id ?? ''
    );
    const enablementGroupedByDevice = this.collectionsService.groupBy(
      enablementCart,
      (enablementCartItem) => enablementCartItem.device.id
    );

    return {
      cartId: cart.id,
      gpCart,
      avdbCart,
      aircraftBundlesCart: aircraftBundleGroupedByAircraft,
      deviceBundlesCart: deviceBundleGroupedByDevice,
      enablementsCart: enablementGroupedByDevice,
      discountCodeCart,
      totalNumberOfItems:
        gpCart.length +
        avdbCart.length +
        aircraftBundlesCart.length +
        deviceBundlesCart.length +
        enablementCart.length +
        discountCodeCart.length
    };
  }

  checkoutCart(cartId: number, billingAccountId?: string): Observable<CheckoutCartResponse> {
    return this.localeService.getSelectedLocale().pipe(
      switchMap((locale) => {
        let params = new HttpParams().set('cartId', cartId).set('locale', locale.wlcLocale);

        if (billingAccountId != null) {
          params = params.set('billingAccountId', billingAccountId);
        }

        return this.http
          .post<CheckoutCartResponse>(`${CARTS_URL}checkout`, null, { params })
          .pipe(tap(() => this.headerCartService.refreshCart()));
      })
    );
  }

  getLatestCheckoutCompletedCart(): Observable<Cart> {
    return this.http.get<Cart>(LATEST_COMPLETED_CART_URL);
  }

  private removeItemFromCurrentCart(params: Record<string, any>): Observable<Cart> {
    return this.http.delete<Cart>(PRODUCTS_URL, { params }).pipe(tap(() => this.headerCartService.refreshCart()));
  }

  updateAvdbStartDate(avdbCartItemId: string, startDate: string | null): Observable<Cart> {
    const avdbId = parseInt(avdbCartItemId, PARSEINT_RADIX);
    let params = new HttpParams().set('avdbCartItemId', avdbId.toString());

    if (startDate != null) {
      params = params.set('startDate', startDate);
    }

    return this.updateStartDate(params);
  }

  updateAvdbBundleStartDate(avdbBundleCartItemId: string, startDate: string | null): Observable<Cart> {
    const avdbBundleId = parseInt(avdbBundleCartItemId, PARSEINT_RADIX);
    let params = new HttpParams().set('avdbBundleCartItemId', avdbBundleId.toString());

    if (startDate != null) {
      params = params.set('startDate', startDate);
    }

    return this.updateStartDate(params);
  }

  updateGpStartDate(gpCartItemId: string, startDate: string | null): Observable<Cart> {
    const gpId = parseInt(gpCartItemId, PARSEINT_RADIX);
    let params = new HttpParams().set('gpCartItemId', gpId.toString());

    if (startDate != null) {
      params = params.set('startDate', startDate);
    }

    return this.updateStartDate(params);
  }

  private updateStartDate(params: Record<string, any>): Observable<Cart> {
    return this.http.put<Cart>(`${CURRENT_CART_URL}/update-start-date`, null, { params });
  }
}
